import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Nav, 
  Card, 
  Navbar,
  Dropdown,
  Button,
  OverlayTrigger,
  Tooltip,
  Modal,
  CloseButton
} from 'react-bootstrap';
import { 
  BoxSeam, 
  CurrencyDollar, 
  CreditCard, 
  GraphUp,
  ListUl,
  X
} from 'react-bootstrap-icons';
import { useAuth } from '../context/AuthContext';
import StockManagement from './StockManagement';
import SalesManagement from './SalesManagement';
import CreditsList from './CreditsList';
import ExpenseList from './ExpenseList';
import './Dashboard.css';
import MonthlyAnalytics from './MonthlyAnalytics';
import AnalyticsDashboard from "./AnalyticsDashboard"

const ProfileDropdown = () => {
  const { user } = useAuth();

  return (
    <Dropdown align="end">
      <Dropdown.Toggle 
        variant="link" 
        id="dropdown-profile"
        className="d-flex align-items-center gap-2 text-decoration-none p-0"
      >
        <div className="avatar-circle bg-primary text-white d-flex align-items-center justify-content-center">
          {user?.profile?.displayName?.[0] || user?.email?.[0]}
        </div>
        <div className="d-none d-md-block">
          <span className="text-dark fw-medium">{user?.profile?.displayName}</span>
          <small className="d-block text-muted">{user?.profile?.businessName}</small>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="shadow-lg border-0 mt-2" style={{ minWidth: '240px' }}>
        {/* Dropdown content */}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('inventory');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const checkMobile = () => {
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);
    if (mobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  };

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Check if onboarding has been seen
    const seenOnboarding = localStorage.getItem('seenDashboardOnboarding');
    if (!seenOnboarding) {
      setTimeout(() => setShowOnboarding(true), 1000);
      localStorage.setItem('seenDashboardOnboarding', 'true');
    }
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const renderActiveComponent = () => {
    switch(activeTab) {
      case 'inventory': return <StockManagement />;
      case 'sales': return <SalesManagement />;
      case 'credits': return <CreditsList />;
      case 'expenses': return <ExpenseList />;
      case 'analytics': return <MonthlyAnalytics />;
      case 'sales analytics': return <AnalyticsDashboard />;
      default: return <StockManagement />;
    }
  };

  const navItems = [
    {
      key: 'inventory',
      icon: <BoxSeam />,
      label: 'Inventory',
      description: 'Add and view your inventory'
    },
    {
      key: 'sales',
      icon: <GraphUp />,
      label: 'Sales',
      description: 'Record sales and print receipts'
    },
    {
      key: 'credits',
      icon: <CreditCard />,
      label: 'Credits',
      description: 'Track customer credits'
    },
    {
      key: 'expenses',
      icon: <ListUl />,
      label: 'Expenses',
      description: 'Record business expenses'
    },
    {
      key: 'analytics',
      icon: <GraphUp />,
      label: 'Income & Expenses',
      description: 'View financial performance'
    },
    {
      key: 'sales analytics',
      icon: <GraphUp />,
      label: 'Sales Analysis',
      description: 'Analyze sales trends'
    }
  ];

  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    }
  };

  return (
    <Container fluid className="p-0 min-vh-100">
      {/* Mobile Header */}
      {isMobile && (
        <Navbar className="bg-dark text-white fixed-top p-2">
          <Container fluid className="px-2">
            <Button 
              variant="link" 
              className="text-white p-0"
              onClick={toggleSidebar}
            >
              <X size={24} />
            </Button>
            <Navbar.Brand className="text-white ms-2 fs-6">
              <CurrencyDollar className="me-1" />FinTrack
            </Navbar.Brand>
            <ProfileDropdown />
          </Container>
        </Navbar>
      )}

      <Row className="g-0">
        {/* Sidebar */}
        {sidebarOpen && (
          <Col 
            lg={2} 
            md={3} 
            className="sidebar bg-dark text-white vh-100 d-flex flex-column p-2 position-fixed"
            style={{ 
              zIndex: 1050, 
              width: isMobile ? '85%' : 'auto',
              left: 0,
              top: isMobile ? '56px' : 0
            }}
          >
            {isMobile && (
              <div className="d-flex justify-content-end mb-2">
                <CloseButton 
                  variant="white" 
                  onClick={() => setSidebarOpen(false)} 
                />
              </div>
            )}

            {!isMobile && (
              <Navbar.Brand className="mb-3 fs-5 fw-bold text-primary">
                <CurrencyDollar className="me-2" />FinTrack
              </Navbar.Brand>
            )}

            <Nav variant="pills" className="flex-column gap-1" activeKey={activeTab}>
              {navItems.map(item => (
                <Nav.Link
                  key={item.key}
                  eventKey={item.key}
                  className="text-white d-flex align-items-center gap-2 p-2"
                  onClick={() => {
                    setActiveTab(item.key);
                    if (isMobile) setSidebarOpen(false);
                  }}
                  active={activeTab === item.key}
                >
                  {item.icon}
                  <div>
                    <div className="fw-medium">{item.label}</div>
                    {isMobile && (
                      <small className="text-muted">{item.description}</small>
                    )}
                  </div>
                </Nav.Link>
              ))}
            </Nav>
          </Col>
        )}

        {/* Sidebar toggle button when closed on mobile */}
        {isMobile && !sidebarOpen && (
          <div className="position-fixed start-0 top-50 mt-4" style={{ zIndex: 1040 }}>
            <Button 
              variant="primary" 
              className="rounded-end-pill py-2"
              onClick={() => setSidebarOpen(true)}
            >
              <BoxSeam size={20} />
            </Button>
          </div>
        )}

        {/* Main Content */}
        <Col 
          lg={10} 
          md={9} 
          className="main-content p-2 p-md-3"
          style={{
            marginLeft: !isMobile && sidebarOpen ? '16.666%' : '0',
            paddingTop: isMobile ? '70px' : '0'
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            {!isMobile && (
              <h2 className="fw-bold m-0 text-dark fs-4">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Management
              </h2>
            )}
            {isMobile && (
              <h3 className="fw-bold m-0 text-dark fs-5">
                {activeTab}
              </h3>
            )}
            <ProfileDropdown />
          </div>

          <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
            <Card.Body className="p-2 p-md-3">
              {renderActiveComponent()}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Onboarding Modal */}
      <Modal
        show={showOnboarding}
        onHide={() => setShowOnboarding(false)}
        centered
        fullscreen={isMobile ? true : undefined}
        className="modal-onboarding"
      >
        <Modal.Header closeButton>
          <Modal.Title>Welcome to FinTrack</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={isMobile ? "fs-6" : ""}>FinTrack helps you manage your business finances:</p>
          <ul className={isMobile ? "fs-6" : ""}>
            {navItems.map(item => (
              <li key={item.key}>
                <strong>{item.label}</strong> - {item.description}
              </li>
            ))}
          </ul>
          <p className={isMobile ? "fs-6" : ""}>Let's get started!</p>
        </Modal.Body>
        <Modal.Footer className={isMobile ? "py-2" : ""}>
          <Button 
            variant="primary" 
            size={isMobile ? "sm" : "md"}
            onClick={() => setShowOnboarding(false)}
          >
            Got it
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Dashboard;